import { Link } from "react-router-dom";
import { Linkedin, MapPin, Building2, Layers, Users, Award, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { blogs } from "@/data/blogs";
import { LINKEDIN_URL } from "@/lib/config";
import FAQ from "@/components/FAQ";
import { aboutFaqs } from "@/data/faqs";
import { PERSON_JSONLD, faqJsonLd } from "@/lib/seo";

const trust = [
    { value: "15+", label: "Years Experience", icon: Award },
    { value: "1000+", label: "Clients & Projects", icon: Users },
    { value: "Bengaluru", label: "Based In", icon: MapPin },
    { value: "Ragi Media", label: "Founder of", icon: Building2 },
    { value: "Brand · Web · UI/UX · Growth", label: "Strategy Focus", icon: Layers },
];

export default function About() {
    const aboutBlogs = blogs.slice(0, 3);
    const { t } = useLang();

    return (
        <div data-testid="about-page" className="relative">
            <Seo
                title="Who I Am · Abhijit JK — Founder of Ragi Media, Bengaluru"
                description="Abhijit JK is a Bengaluru-based Brand & Business Growth Consultant and Founder of Ragi Media, with 15+ years across branding, design, websites, UI/UX and business growth for 1000+ clients across India."
                keywords="Abhijit JK, Ragi Media founder, brand consultant Bengaluru, business growth consultant, about Abhijit JK, startup mentor India"
                image="/images/about-hero.jpg"
                path="/about"
                jsonLd={[PERSON_JSONLD, faqJsonLd(aboutFaqs)]}
            />

            {/* HERO */}
            <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 pt-20 pb-12 lg:grid-cols-12 lg:px-10">
                <div className="lg:col-span-6">
                    <Reveal>
                        <SectionEyebrow>{t("about_eyebrow")}</SectionEyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            data-testid="about-headline"
                            className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tighter sm:text-6xl"
                        >
                            {t("about_pre")} <span className="gold-text">{t("about_gold")}</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-base leading-relaxed text-white/70 md:text-lg">
                            {t("about_sub")}
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href={LINKEDIN_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="about-linkedin-btn"
                                className="gold-button inline-flex items-center gap-2"
                            >
                                <Linkedin size={15} /> {t("btn_visit_linkedin")}
                            </a>
                            <Link
                                to="/booking"
                                data-testid="about-hero-cta"
                                className="ghost-button inline-flex items-center gap-2"
                            >
                                {t("btn_book")}
                            </Link>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="lg:col-span-6">
                    <div className="relative mx-auto max-w-[560px]">
                        <span
                            aria-hidden
                            className="pointer-events-none absolute -top-8 left-1/2 z-0 hidden w-[130%] -translate-x-1/2 select-none text-center font-display text-[58px] font-bold uppercase leading-[0.95] tracking-tight text-white/[0.04] lg:block"
                        >
                            Bengaluru Based Brand &amp; Business Growth Consultant
                        </span>
                        <div className="relative z-10 overflow-hidden rounded-lg">
                            <img
                                src="/images/about-hero.jpg"
                                alt="Abhijit JK, Founder of Ragi Media — Brand & Business Growth Consultant in Bengaluru, India"
                                data-testid="about-founder-image"
                                className="h-full w-full object-cover"
                            />
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* TRUST */}
            <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {trust.map((t, i) => {
                        const Icon = t.icon;
                        return (
                            <Reveal key={t.label} delay={i * 0.06}>
                                <div
                                    data-testid={`trust-card-${i}`}
                                    className="glass glass-hover h-full rounded-md p-6 text-center"
                                >
                                    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-sm bg-gold-300/10 text-gold-300">
                                        <Icon size={18} />
                                    </div>
                                    <p className="mt-5 font-display text-2xl font-semibold gold-text">
                                        {t.value}
                                    </p>
                                    <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/55">
                                        {t.label}
                                    </p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* STORY */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal>
                    <SectionEyebrow>My Journey</SectionEyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-5 max-w-4xl font-display text-3xl font-medium leading-tight sm:text-4xl lg:text-5xl">
                        From <span className="gold-text">designer</span> to{" "}
                        <span className="gold-text">founder</span> to{" "}
                        <span className="gold-text">growth consultant.</span>
                    </h2>
                </Reveal>
                <div className="mt-10 grid gap-10 lg:grid-cols-12">
                    <Reveal delay={0.15} className="lg:col-span-6">
                        <p className="text-base leading-relaxed text-white/70 md:text-lg">
                            I started in the design and creative industry and grew
                            into a founder who understands business from the ground
                            level. Through Ragi Media, I have worked with all kinds of
                            businesses and seen the same problems again and again:
                            unclear positioning, weak branding, poor digital presence,
                            no lead system, and no real roadmap.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2} className="lg:col-span-6">
                        <p className="text-base leading-relaxed text-white/70 md:text-lg">
                            My consultation exists to solve exactly these problems —
                            with honest diagnosis, sharp strategy, and a clear,
                            step-by-step plan you can start acting on the very next
                            day.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* BLOGS */}
            <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Reveal>
                            <SectionEyebrow>From My Desk</SectionEyebrow>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-display text-3xl font-medium sm:text-4xl">
                                Things I write about
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <Link to="/blog" className="ghost-button inline-flex items-center gap-2">
                            View All <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
                <div className="mt-12 grid gap-8 md:grid-cols-3">
                    {aboutBlogs.map((b, i) => (
                        <Reveal key={b.slug} delay={i * 0.08}>
                            <Link
                                to={`/blog/${b.slug}`}
                                data-testid={`about-blog-${b.slug}`}
                                className="group glass glow-border block h-full overflow-hidden rounded-lg"
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                                </div>
                                <div className="p-6">
                                    <p className="text-[11px] uppercase tracking-[0.2em] text-gold-300/80">{b.category}</p>
                                    <h3 className="mt-3 font-display text-lg leading-snug">{b.title}</h3>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <FAQ items={aboutFaqs} eyebrow="FAQ" title="About Abhijit JK —" gold="frequently asked." />
        </div>
    );
}
